import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import PermissionMatrix from '../../Components/PermissionMatrix';
import DOMAINS from '../../constants/domains';

export default function RoleForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [catalog, setCatalog] = useState([]);
  const [form, setForm] = useState({
    code: '', domain: DOMAINS[0].code, name: '', description: '',
  });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadCatalog = async (domain) => {
    const catalogData = await api.getPermissionCatalog({ domain });
    setCatalog(catalogData);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isEdit) {
          const role = await api.getRoleDetail({ id });
          setForm({
            code: role.code, domain: role.domain, name: role.name, description: role.description || '',
          });
          setSelectedIds(new Set(role.permissionIds));
          await loadCatalog(role.domain);
        } else {
          await loadCatalog(DOMAINS[0].code);
        }
      } catch (err) {
        console.error(err);
        showToast('Gagal memuat data role.', 'danger');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleDomainChange = async (domain) => {
    setForm((f) => ({ ...f, domain }));
    setSelectedIds(new Set()); // ganti domain -> pilihan permission lama (domain lain) tidak relevan lagi
    await loadCatalog(domain);
  };

  const handleTogglePermission = (perm) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(perm.id)) next.delete(perm.id);
      else next.add(perm.id);
      return next;
    });
  };

  const handleSelectAllInModule = (mod) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = mod.permissions.every((p) => next.has(p.id));
      mod.permissions.forEach((p) => {
        if (allSelected) next.delete(p.id);
        else next.add(p.id);
      });
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const permissionIds = Array.from(selectedIds);

      if (isEdit) {
        await api.updateRole({
          id, name: form.name, description: form.description, permissionIds,
        });
        showToast('Role berhasil diperbarui!', 'success');
      } else {
        await api.addRole({
          code: form.code.toUpperCase(),
          domain: form.domain,
          name: form.name,
          description: form.description,
          permissionIds,
        });
        showToast('Role berhasil dibuat!', 'success');
      }
      navigate('/roles');
    } catch (err) {
      console.error(err);
      showToast(`Gagal menyimpan role: ${err.message || err}`, 'danger');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-5 text-center text-muted">
        <div className="spinner-border text-primary mb-3" />
        <p>Memuat...</p>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0">
      <nav aria-label="breadcrumb" className="mb-4">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/roles" className="text-decoration-none">Roles</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {isEdit ? 'Kelola Role' : 'Tambah Role'}
          </li>
        </ol>
      </nav>

      <form onSubmit={handleSubmit}>
        <div className="card border-0 shadow-sm rounded-4 mb-4">
          <div className="card-body p-4">
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label fw-semibold">Domain</label>
                <select
                  className="form-select"
                  value={form.domain}
                  disabled={isEdit}
                  onChange={(e) => handleDomainChange(e.target.value)}
                  required
                >
                  {DOMAINS.map((d) => (
                    <option key={d.code} value={d.code}>{d.code} — {d.label}</option>
                  ))}
                </select>
                {isEdit && <small className="text-muted">Domain tidak dapat diubah.</small>}
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">Kode Role</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={`${form.domain}_ASSESSOR`}
                  value={form.code}
                  disabled={isEdit}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                  required
                />
                {isEdit && <small className="text-muted">Kode role tidak dapat diubah.</small>}
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">Nama Role</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={`Assessor ${form.domain}`}
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  required
                />
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold">Deskripsi</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
          <div className="card-header bg-white border-0 pt-4 px-4">
            <h6 className="fw-bold mb-0">Permission Matrix — Domain {form.domain}</h6>
            <small className="text-muted">Pilih modul dan aksi yang diizinkan untuk role ini</small>
          </div>
          <div className="card-body p-4">
            <div className="d-flex flex-wrap gap-2 mb-3">
              {catalog.map((mod) => (
                <button
                  key={mod.module}
                  type="button"
                  className="btn btn-sm btn-outline-secondary rounded-pill"
                  onClick={() => handleSelectAllInModule(mod)}
                >
                  {mod.permissions.every((p) => selectedIds.has(p.id)) ? 'Kosongkan' : 'Pilih semua'} {mod.moduleLabel}
                </button>
              ))}
            </div>
            <PermissionMatrix
              catalog={catalog}
              selectedIds={selectedIds}
              onToggle={handleTogglePermission}
            />
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 mt-4 mb-4">
          <Link to="/roles" className="btn btn-light rounded-pill px-4 btn-sm fw-medium text-muted">
            Batal
          </Link>
          <button type="submit" className="btn btn-primary rounded-pill px-4 btn-sm fw-medium shadow-sm" disabled={saving}>
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Menyimpan...
              </>
            ) : (
              <>
                <i className="bi bi-check-lg me-1"></i> Simpan Role
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
